import type { StatsDTO } from '@/types/db';
import { StatTile } from './StatTile';

export interface StatsStripProps {
  /** From `/api/stats`. `null` while loading is the caller's responsibility (use LoadingSkeleton). */
  stats: StatsDTO;
  /**
   * Filtered `Shown` count + `Avg Fit` are computed client-side from the
   * current rankings list (Plan 05-05 wires this); pass them in here.
   * In Plan 05-04 the showcase route passes static mocks.
   */
  shown: number;
  avgFit: number | null;
  className?: string;
}

/**
 * `<StatsStrip>` — top-of-page row of 5 tiles per D5 + UI-SPEC § 3.6.
 *
 * Tile order (locked):
 *   1. Total Properties — integer (e.g. `70`)         — static
 *   2. Shown            — integer post-filter         — static
 *   3. Avg Fit          — integer 0–100               — static
 *   4. Top Fit          — short address · score       — clickable → /properties/:id
 *   5. Freshest         — short address · `Nd`        — clickable → /properties/:id
 *
 * Cold start (`stats.total === 0`): all tiles show `—` value plus a
 * single subtitle CTA on the first tile pointing operators to
 * `/app/submit`. (DASH-06 cold-start handling.)
 *
 * Layout: `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6
 * gap-4`. ARIA per § 9.3: `role="region" aria-label="Dashboard summary
 * statistics"`.
 */
export function StatsStrip({ stats, shown, avgFit, className }: StatsStripProps) {
  const isColdStart = stats.total === 0;

  if (isColdStart) {
    return (
      <section
        role="region"
        aria-label="Dashboard summary statistics"
        className={
          'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 ' +
          (className ?? '')
        }
      >
        <StatTile
          label="Total"
          value="—"
          subtitle="No properties yet — submit your first address."
        />
        <StatTile label="Shown" value="—" />
        <StatTile label="Avg" value="—" />
        <StatTile label="Top Fit" value="—" className="lg:col-span-3" />
      </section>
    );
  }

  const topFitText = stats.top_fit
    ? stats.top_fit.address.replace(/(\b\d{5}(?:-\d{4})?)\b.*$/, '$1')
    : '—';
  const topFitHref = stats.top_fit ? `/properties/${stats.top_fit.id}` : undefined;

  return (
    <section
      role="region"
      aria-label="Dashboard summary statistics"
      className={
        'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 ' +
        (className ?? '')
      }
    >
      <StatTile label="Total" value={stats.total} />
      <StatTile label="Shown" value={shown} />
      <StatTile label="Avg" value={avgFit == null ? '—' : Math.round(avgFit)} />
      <StatTile
        label="Top Fit"
        value={topFitText}
        clickable={Boolean(topFitHref)}
        href={topFitHref}
        className="lg:col-span-3"
      />
    </section>
  );
}
